import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { tap, map, catchError, shareReplay, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { endpoints } from '../../environments/endpoints';
import { AuthResponse, LaravelDefaultResponse } from '../Interfaces/response.interface';
import { User } from '../Models/user.model';
import { ApiService } from './api.service';
import { AuthTokenService } from './auth-token.service';
import { LocalStorageService } from './local-storage.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _api = inject(ApiService);
  private readonly _router = inject(Router);
  private readonly _authToken = inject(AuthTokenService);
  private readonly _localStorage = inject(LocalStorageService);
  private authInitialization$?: Observable<boolean>;

  private readonly currentUserSubject = new BehaviorSubject<User | null>(null);
  readonly currentUser$ = this.currentUserSubject.asObservable();

  private readonly isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  readonly isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  private readonly authInitializedSubject = new BehaviorSubject<boolean>(false);
  readonly authInitialized$ = this.authInitializedSubject.asObservable();

  initializeAuth(): Observable<boolean> {
    if (this.authInitializedSubject.value) {
      return of(this.isAuthenticatedSubject.value);
    }

    if (!this.authInitialization$) {
      this.authInitialization$ = this.bootstrapAuth().pipe(
        map(user => !!user),
        catchError(() => of(false)),
        tap(isAuthenticated => {
          this.authInitializedSubject.next(true);
          this.isAuthenticatedSubject.next(isAuthenticated);
        }),
        shareReplay(1),
      );
    }

    return this.authInitialization$;
  }

  private bootstrapAuth(): Observable<User | null> {
    if (this._authToken.hasValidToken()) {
      return this.me().pipe(catchError(error => this.handleInitializationError(error)));
    }

    if (this._authToken.hasRefreshToken()) {
      return this.refreshToken().pipe(
        switchMap(() => this.me()),
        catchError(error => this.handleInitializationError(error)),
      );
    }

    this.clearAuth();
    return of(null);
  }

  private handleInitializationError(error: unknown): Observable<null> {
    if (this.isAuthenticationError(error)) {
      this.clearAuth();
    }

    return of(null);
  }

  private isAuthenticationError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return true;
    }

    return error.status === 401 || error.status === 403;
  }

  setReturnUrl(returnUrl: string): void {
    this._authToken.setReturnUrl(returnUrl);
  }

  exchangeCode(
    code: string,
    handoffVerifier: string | null,
  ): Observable<LaravelDefaultResponse<AuthResponse>> {
    const request = this._api.post<LaravelDefaultResponse<AuthResponse>>(endpoints.auth.exchangeCode, {
      body: { code, handoff_verifier: handoffVerifier },
      skipLoading: true,
      withCredentials: true,
    });

    return this._api
      .run<LaravelDefaultResponse<AuthResponse>>(request)
      .pipe(tap(response => this.setAuth(response.data)));
  }

  me(): Observable<User> {
    const request = this._api.get<LaravelDefaultResponse<User>>(endpoints.onboarding.me, {
      skipLoading: true,
    });

    return this._api.run<LaravelDefaultResponse<User>>(request).pipe(
      map(response => new User(response.data)),
      tap(user => {
        this.currentUserSubject.next(user);
        this.isAuthenticatedSubject.next(true);
      }),
    );
  }

  refreshToken(): Observable<AuthResponse> {
    const refresh_token = this._authToken.getRefreshToken();

    if (!refresh_token) {
      return throwError(() => new Error('No refresh token available.'));
    }

    const request = this._api.post<LaravelDefaultResponse<AuthResponse>>(endpoints.auth.refresh, {
      body: { refresh_token },
      skipLoading: true,
    });

    return this._api.run<LaravelDefaultResponse<AuthResponse>>(request).pipe(
      map(response => response.data),
      tap(authData => this.setAuth(authData)),
    );
  }

  logout(): void {
    this.revokeRefreshToken().subscribe({
      next: () => {
        this.clearAuth();
        this._router.navigate(['/login']);
      },
    });
  }

  terminateSession(): Observable<boolean> {
    return this.revokeRefreshToken().pipe(tap(() => this.clearLocalSession()));
  }

  private clearLocalSession(): void {
    this.clearAuth();
    this._localStorage.clear();
    this._localStorage.clearSessionStorage();
  }

  private revokeRefreshToken(): Observable<boolean> {
    const refresh_token = this._authToken.getRefreshToken();

    if (!refresh_token) {
      return of(false);
    }

    const request = this._api.post<unknown>(endpoints.auth.logout, {
      body: { refresh_token },
      skipLoading: true,
    });

    return this._api.run<unknown>(request).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }

  setAuth(auth_data: AuthResponse): void {
    this._authToken.setToken(auth_data);
    this.isAuthenticatedSubject.next(true);
    this.authInitializedSubject.next(true);
  }

  getToken(): string | null {
    return this._authToken.getToken();
  }

  isTokenExpired(): boolean {
    return this._authToken.isTokenExpired();
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value && this._authToken.hasValidToken();
  }

  getAndClearReturnUrl(): string {
    return this._authToken.getAndClearReturnUrl();
  }

  clearAuth(): void {
    this._authToken.clearToken();
    this.currentUserSubject.next(null);
    this.isAuthenticatedSubject.next(false);
  }
}
